import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, CalendarClock, Download, Search, Ban, RefreshCw, Braces } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  type CalendlyMeeting,
  calendlyMeetingStatusClass,
  calendlyStatusLabel,
} from '../../lib/subscriberCalendly';

interface CalendlyMeetingsResponse {
  meetings?: CalendlyMeeting[];
  error?: string;
}

const PAGE_SIZE = 15;

function formatMeetingWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

interface CalendlyMeetingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Existing subscriber emails (lowercase) to skip duplicates */
  existingEmails: Set<string>;
  /** Opens the add-subscriber modal with this meeting prefilled (no DB write here). */
  onImport: (meeting: CalendlyMeeting) => void;
  /** Label for duplicate state */
  duplicateLabel?: string;
}

export default function CalendlyMeetingsModal({
  isOpen,
  onClose,
  existingEmails,
  onImport,
  duplicateLabel = 'In subscribers',
}: CalendlyMeetingsModalProps) {
  const [ignoredIds, setIgnoredIds] = useState<Set<string>>(new Set());
  const [nameSearch, setNameSearch] = useState('');
  const [meetings, setMeetings] = useState<CalendlyMeeting[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [debugMeeting, setDebugMeeting] = useState<CalendlyMeeting | null>(null);
  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);

  const loadMeetings = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const { data, error } = await supabase.functions.invoke<CalendlyMeetingsResponse>(
        'calendly-meetings',
        { body: {} },
      );

      if (error) {
        throw new Error(error.message || 'Failed to invoke calendly-meetings');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      const list = Array.isArray(data?.meetings) ? data.meetings : [];
      setMeetings(list);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Could not load Calendly meetings';
      setFetchError(message);
      setMeetings([]);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setIgnoredIds(new Set());
    setNameSearch('');
    setFetchError(null);
    setDebugMeeting(null);
    setVisibleLimit(PAGE_SIZE);
    void loadMeetings();
  }, [isOpen, loadMeetings]);

  useEffect(() => {
    setVisibleLimit(PAGE_SIZE);
  }, [nameSearch, meetings]);

  useEffect(() => {
    if (!isOpen) setDebugMeeting(null);
  }, [isOpen]);

  const meetingsSorted = useMemo(() => {
    const q = nameSearch.trim().toLowerCase();
    let list = meetings.filter((m) => !ignoredIds.has(m.id));
    list = q ? list.filter((m) => m.name.toLowerCase().includes(q)) : list;
    return [...list].sort(
      (a, b) => new Date(b.lastContactAt).getTime() - new Date(a.lastContactAt).getTime(),
    );
  }, [meetings, nameSearch, ignoredIds]);

  const meetingsVisible = useMemo(
    () => meetingsSorted.slice(0, visibleLimit),
    [meetingsSorted, visibleLimit],
  );

  const hasMore = meetingsVisible.length < meetingsSorted.length;
  const totalCount = meetingsSorted.length;

  if (!isOpen) return null;

  const handleImportClick = (meeting: CalendlyMeeting) => {
    const emailLower = meeting.email.trim().toLowerCase();
    if (existingEmails.has(emailLower)) {
      toast.error('This contact is already a subscriber');
      return;
    }
    onImport(meeting);
  };

  const handleIgnore = (meeting: CalendlyMeeting) => {
    setIgnoredIds((prev) => new Set(prev).add(meeting.id));
    toast.info(`Hidden ${meeting.name} for this session`);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full max-h-[min(95vh,1100px)] flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-3">
            <CalendarClock className="w-7 h-7 text-blue-500" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Calendly meetings</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Loaded from your Calendly account (newest first).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => void loadMeetings()}
              disabled={loading}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
              aria-label="Refresh meetings"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="px-6 pt-4 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="search"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              placeholder="Search by name…"
              disabled={loading || !!fetchError}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6 pt-4">
          {fetchError ? (
            <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-6 text-center">
              <p className="text-sm text-red-800 dark:text-red-200 mb-3">{fetchError}</p>
              <button
                type="button"
                onClick={() => void loadMeetings()}
                className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg"
              >
                Try again
              </button>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-gray-400 gap-3">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
              <p className="text-sm">Loading meetings from Calendly…</p>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
              {totalCount === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {meetings.length === 0
                    ? 'No upcoming or past meetings found in Calendly.'
                    : 'No meetings match that name.'}
                </div>
              ) : (
                meetingsVisible.map((meeting) => {
                  const emailLower = meeting.email.trim().toLowerCase();
                  const alreadyExists = existingEmails.has(emailLower);

                  return (
                    <div
                      key={meeting.id}
                      className="p-4 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4"
                    >
                      <div className="min-w-0 space-y-2 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">{meeting.name}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDebugMeeting(meeting);
                            }}
                            className="inline-flex shrink-0 p-1 rounded text-gray-400 hover:text-violet-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="View raw JSON (debug)"
                            aria-label="View raw JSON for this meeting"
                          >
                            <Braces className="w-4 h-4" strokeWidth={2} />
                          </button>
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${calendlyMeetingStatusClass(meeting.meetingStatus)}`}
                          >
                            {calendlyStatusLabel(meeting.meetingStatus)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 break-all">
                          {meeting.email || '—'}
                        </div>
                        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
                          <div className="text-gray-700 dark:text-gray-300">
                            <span className="text-gray-500 dark:text-gray-500">Event: </span>
                            {meeting.eventName}
                          </div>
                          <div className="text-gray-600 dark:text-gray-400">
                            <span className="text-gray-500 dark:text-gray-500">Last contact: </span>
                            {formatMeetingWhen(meeting.lastContactAt)}
                          </div>
                          <div className="text-gray-600 dark:text-gray-400">
                            <span className="text-gray-500 dark:text-gray-500">Duration: </span>
                            {meeting.durationMinutes} min
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0 lg:pt-1 flex flex-wrap gap-2 justify-end">
                        <button
                          type="button"
                          disabled={alreadyExists}
                          onClick={() => handleImportClick(meeting)}
                          className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {alreadyExists ? duplicateLabel : 'Import'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleIgnore(meeting)}
                          className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <Ban className="w-4 h-4 mr-2" />
                          Ignore
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
          {!loading && !fetchError && totalCount > 0 ? (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-1">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Showing {meetingsVisible.length} of {totalCount}
                {nameSearch.trim() ? ' (filtered)' : ''}
              </p>
              {hasMore ? (
                <button
                  type="button"
                  onClick={() => setVisibleLimit((prev) => prev + PAGE_SIZE)}
                  className="text-sm font-medium px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 w-full sm:w-auto"
                >
                  More ({Math.min(PAGE_SIZE, totalCount - meetingsVisible.length)} more)
                </button>
              ) : totalCount > PAGE_SIZE ? (
                <span className="text-xs text-gray-400 dark:text-gray-500">All loaded</span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {debugMeeting ? (
        <div
          className="fixed inset-0 z-[110] flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="calendly-json-debug-title"
          onClick={() => setDebugMeeting(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[min(85vh,640px)] flex flex-col border border-gray-200 dark:border-gray-700 min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h3
                id="calendly-json-debug-title"
                className="text-sm font-semibold text-gray-900 dark:text-white"
              >
                Meeting JSON (debug)
              </h3>
              <button
                type="button"
                onClick={() => setDebugMeeting(null)}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close debug panel"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-all">
              {JSON.stringify(debugMeeting, null, 2)}
            </pre>
          </div>
        </div>
      ) : null}
    </div>
  );
}
